import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Zap, Download, Package, AlertTriangle, Loader2, Webhook } from "lucide-react";

export default function SmartSync() {
  const [overview, setOverview] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");

  const loadOverview = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await API.get("/tools/smart-sync/overview");
      setOverview(data);
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const loadInventory = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await API.get("/tools/smart-sync/inventory");
      setInventory(data);
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const exportCSV = async () => {
    try {
      const response = await API.get("/tools/smart-sync/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.download = "inventory_export.csv"; link.click();
      toast.success("CSV exported!");
    } catch (e) { toast.error("Export failed"); }
  };

  const loadWebhookLogs = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/tools/smart-sync/webhook-logs");
      setWebhookLogs(data);
    } catch (e) { setError(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const TABS = [
    { id: "overview", label: "Sync Overview", action: loadOverview },
    { id: "inventory", label: "Inventory", action: loadInventory },
    { id: "webhooks", label: "Webhook Logs", action: loadWebhookLogs },
  ];

  const handleTab = (t) => { setTab(t.id); t.action(); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0]" data-testid="smart-sync-page">
      <nav className="border-b border-[#1E1E1E]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase">NRD<span className="text-[#00FF41]">.</span></Link>
            <span className="text-xs font-mono text-[#00FF41]">/ SMART-SYNC</span>
          </div>
          <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#00FF41] flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#00FF41] mb-1">Smart-Sync API</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Universal Data Bridge</h1>
        </div>
        <div className="flex gap-0 border border-[#1E1E1E] mb-8">
          {TABS.map(t => (
            <button key={t.id} onClick={() => handleTab(t)}
              className={`px-5 py-3 text-sm font-medium border-r border-[#1E1E1E] last:border-r-0 transition-colors ${tab === t.id ? 'bg-[#00FF41] text-[#0A0A0A]' : 'text-slate-400 hover:text-[#00FF41] hover:bg-[#111111]'}`}
              data-testid={`sync-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        {error && <div className="p-4 bg-red-900/30 border border-red-800 text-sm text-red-400 mb-6">{error}</div>}
        {loading && <div className="flex justify-center py-20"><Loader2 size={24} className="text-[#00FF41] animate-spin" /></div>}

        {!loading && tab === "overview" && overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#1E1E1E] bg-[#111111]">
            {[
              { label: "Products", val: overview.total_products, icon: Package },
              { label: "Variants", val: overview.total_variants },
              { label: "Total Inventory", val: overview.total_inventory.toLocaleString(), color: "text-[#00FF41]" },
              { label: "Active", val: overview.active_products },
              { label: "Draft", val: overview.draft_products },
              { label: "Low Stock", val: overview.low_stock_count, color: "text-yellow-400" },
              { label: "Out of Stock", val: overview.out_of_stock_count, color: "text-red-400" },
              { label: "Last Synced", val: new Date(overview.last_synced).toLocaleTimeString(), color: "text-[#00FF41]" },
            ].map((s, i) => (
              <div key={i} className={`p-5 border-r border-b border-[#1E1E1E]`}>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">{s.label}</p>
                <p className={`font-heading text-2xl font-black ${s.color || ''}`}>{s.val}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "inventory" && inventory && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={exportCSV} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-xs font-medium hover:bg-[#00FF41] hover:text-[#0A0A0A] transition-colors" data-testid="export-csv-btn">
                <Download size={14} /> Export CSV
              </button>
            </div>
            <div className="border border-[#1E1E1E] bg-[#111111] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1E1E1E] bg-[#0A0A0A]">
                    {["Product", "SKU", "Price", "Stock", "Status"].map(h => (
                      <th key={h} className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inventory.map((item, i) => (
                    <tr key={i} className="border-b border-[#1E1E1E] last:border-0">
                      <td className="p-3">{item.title} <span className="text-xs text-slate-500">/ {item.variant_title}</span></td>
                      <td className="p-3 text-xs font-mono text-slate-400">{item.sku || '-'}</td>
                      <td className="p-3 text-[#00FF41] font-mono">${item.price}</td>
                      <td className="p-3">
                        <span className={`font-mono ${item.inventory_quantity <= 0 ? 'text-red-400' : item.inventory_quantity <= 5 ? 'text-yellow-400' : 'text-[#00FF41]'}`}>
                          {item.inventory_quantity}
                        </span>
                      </td>
                      <td className="p-3"><span className={`text-xs font-mono px-2 py-0.5 ${item.status === 'active' ? 'text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30' : 'text-slate-400 bg-slate-800 border border-slate-700'}`}>{item.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && tab === "webhooks" && webhookLogs !== null && (
          <div className="border border-[#1E1E1E] bg-[#111111]">
            <div className="border-b border-[#1E1E1E] px-5 py-3 bg-[#0A0A0A] flex items-center gap-2">
              <Webhook size={14} className="text-[#00FF41]" />
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Incoming Webhook Events ({webhookLogs.length})</p>
            </div>
            {webhookLogs.length === 0 ? <p className="text-center py-8 text-sm text-slate-500">No webhook events yet</p> :
              webhookLogs.map((log, i) => (
                <div key={i} className="px-5 py-3 border-b border-[#1E1E1E] last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-[#00FF41]">{log.source}</span>
                    <span className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  <pre className="text-xs text-slate-400 bg-[#0A0A0A] p-2 overflow-x-auto">{JSON.stringify(log.data, null, 2)}</pre>
                </div>
              ))
            }
          </div>
        )}

        {!loading && !overview && !inventory && !webhookLogs && (
          <div className="text-center py-20 text-slate-500"><Zap size={48} className="mx-auto mb-4 opacity-30" /><p>Select a tab to start syncing</p></div>
        )}
      </div>
    </div>
  );
}
